<template>
  <div class="flex w-full bg-img vx-row no-gutter justify-center items-center">
    <div class="vx-col sm:w-1/2 md:w-1/2 lg:w-2/5 m-4">
      <vx-card>
        <vs-divider position="center">
          <h5>Generate Payment Link</h5>
        </vs-divider>

        <div class="subscription">
          <div class="booking-id-section">
            <p class="section-description">Enter booking ID to generate payment link</p>
            
            <vs-input
              type="number"
              label="Booking ID"
              placeholder="Enter Booking ID"
              v-model="booking_id"
              class="booking-input"
              icon-pack="feather"
              icon="icon-hash"
              icon-after
            />

            <vs-button 
              class="generate-button" 
              @click="go_generate()" 
              :disabled="!booking_id || isGenerating"
              :icon-pack="isGenerating ? 'feather' : 'feather'"
              :icon="isGenerating ? 'icon-loader' : 'icon-link'"
              icon-after
            >
              {{ isGenerating ? 'Generating...' : 'Generate Link' }}
            </vs-button>
          </div>

          <!-- Booking Details Section -->
          <div class="mt-4" v-if="payment_link">
            <vs-divider position="center">
              <h5>Booking Details</h5>
            </vs-divider>

            <div class="booking-details-card">
              <div class="detail-item">
                <div class="detail-icon pickup-icon">
                  <feather-icon icon="MapPinIcon" svgClasses="w-3.5 h-3.5" />
                </div>
                <div class="detail-content">
                  <span class="detail-label">Pickup Location</span>
                  <span class="detail-value">{{ source || 'N/A' }}</span>
                </div>
              </div>
              <div class="detail-item">
                <div class="detail-icon destination-icon">
                  <feather-icon icon="NavigationIcon" svgClasses="w-3.5 h-3.5" />
                </div>
                <div class="detail-content">
                  <span class="detail-label">Destination</span>
                  <span class="detail-value">{{ destination || 'N/A' }}</span>
                </div>
              </div>
              <div class="detail-item">
                <div class="detail-icon time-icon">
                  <feather-icon icon="ClockIcon" svgClasses="w-3.5 h-3.5" />
                </div>
                <div class="detail-content">
                  <span class="detail-label">Booked At</span>
                  <span class="detail-value">{{ date_time || 'N/A' }}</span>
                </div>
              </div>
              <div class="detail-item">
                <div class="detail-icon pickup-time-icon">
                  <feather-icon icon="CalendarIcon" svgClasses="w-3.5 h-3.5" />
                </div>
                <div class="detail-content">
                  <span class="detail-label">Pickup Due Time</span>
                  <span class="detail-value">{{ pickup_due_time || 'N/A' }}</span>
                </div>
              </div>
              <div class="detail-item price-item">
                <div class="detail-icon price-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="cash-icon">
                    <rect x="2" y="6" width="20" height="12" rx="2"></rect>
                    <line x1="6" y1="10" x2="18" y2="10"></line>
                    <line x1="6" y1="14" x2="18" y2="14"></line>
                    <circle cx="12" cy="12" r="1"></circle>
                  </svg>
                </div>
                <div class="detail-content">
                  <span class="detail-label">Price</span>
                  <span class="detail-value price-value">£{{ price || '0.00' }}</span>
                </div>
              </div>
              <div class="detail-item payment-link-item">
                <div class="detail-icon payment-link-icon">
                  <feather-icon icon="LinkIcon" svgClasses="w-3.5 h-3.5" />
                </div>
                <div class="detail-content">
                  <span class="detail-label">Payment Link</span>
                  <a 
                    :href="payment_link" 
                    target="_blank" 
                    class="detail-value payment-link-value"
                  >
                    {{ payment_link }}
                    <feather-icon 
                      icon="ExternalLinkIcon" 
                      svgClasses="w-3 h-3 ml-1 inline-block" 
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="options-alignment" v-if="payment_link">
            <vs-button 
              color="success" 
              icon-pack="feather" 
              icon="icon-smartphone" 
              icon-after 
              @click="smsPrompt = true"
            >
              Send SMS
            </vs-button>
            <vs-button 
              color="success" 
              icon-pack="feather" 
              icon="icon-mail" 
              icon-after 
              @click="emailPrompt = true"
            >
              Send Email
            </vs-button>
          </div>
        </div>
      </vx-card>

      <!-- SMS Prompt -->
      <vs-prompt
        title="Send SMS to Customer"
        accept-text="Send"
        cancel-text="Cancel"
        @accept="send_sms"
        :active.sync="smsPrompt"
      >
        <div class="con-exemple-prompt">
          <p class="mb-3 text-gray-600">Enter the phone number</p>
          <vs-input 
            class="w-full mt-2" 
            placeholder="+447*******" 
            v-model="customer_telephone"
            label="Phone Number"
          />
        </div>
      </vs-prompt>

      <!-- Email Prompt -->
      <vs-prompt
        title="Send Email to Customer"
        accept-text="Send"
        cancel-text="Cancel"
        @accept="send_email"
        :active.sync="emailPrompt"
      >
        <div class="con-exemple-prompt">
          <p class="mb-3 text-gray-600">Enter email address</p>
          <vs-input 
            class="w-full mt-2" 
            placeholder="john@smith.com" 
            v-model="customer_email"
            label="Email Address"
            type="email"
          />
        </div>
      </vs-prompt>
    </div>
  </div>
</template>

<script>
import { _generate_payment_link, _send_sms, _send_email } from "@/services/Smspay";
import { _recordAnalytics } from "@/services/analytics";

export default {
  data() {
    return {
      booking_id: "",
      portal_id: "",
      payment_link: "",
      source: "",
      destination: "",
      date_time: "",
      price: "",
      current_company: this.$store.state.company.companyID,
      smsPrompt: false,
      emailPrompt: false,
      customer_telephone: "",
      customer_email: "",
      pickup_due_time: null,
      systemId: this.$store.state.AppActiveUser.systemId,
      remote_system_id: null,
      company_name: null,
      isGenerating: false,
    };
  },
  watch: {
    "$store.state.company.companyID"(val) {
      this.current_company = val;
    },
  },
  methods: {
    go_generate() {
      // Reset previous data
      this.payment_link = "";
      this.source = "";
      this.destination = "";
      this.date_time = "";
      this.price = "";
      this.pickup_due_time = null;
      this.customer_telephone = "";
      this.customer_email = "";
      this.company_name = null;
      this.remote_system_id = null;

      if (!this.booking_id) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please enter a booking ID",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
        });
        return;
      }

      this.isGenerating = true;
      // this.$vs.loading();

      _generate_payment_link({
        companyId: this.current_company,
        bookingId: this.booking_id,
      })
        .then((res) => {
          if (res.data && res.data.obj) {
            this.payment_link = res.data.obj.message;
            this.source = res.data.obj.pickup;
            this.destination = res.data.obj.destination;
            this.date_time = res.data.obj.date_time;
            this.pickup_due_time = res.data.obj.pickup_due_time;
            this.price = res.data.obj.price;
            this.customer_telephone = res.data.obj.customer_phone;
            this.customer_email = res.data.obj.customer_email;
            this.company_name = res.data.obj.company_name;
            this.remote_system_id = res.data.obj.system_id;

            // Record analytics
            if (this.remote_system_id) {
              _recordAnalytics({
                booking_id: this.booking_id,
                button_type: 'generate_link',
                system_id: this.remote_system_id
              }).catch(() => {
                // Silently fail analytics
              });
            }

            this.$vs.notify({
              title: "Success",
              text: "Payment link generated successfully",
              iconPack: "feather",
              icon: "icon-check-circle",
              color: "success",
            });
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          // this.$vs.loading.close();
          this.isGenerating = false;
        });
    },

    send_sms() {
      if (!this.customer_telephone) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please enter a phone number",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
        });
        return;
      }

      const message = `Here's your payment link for Booking ID ${this.booking_id} at ${this.pickup_due_time || 'scheduled time'}. Link: ${this.payment_link}`;
      
      const data = {
        phonenumber: this.customer_telephone,
        message: message,
        bookingId: this.booking_id,
        systemId: this.remote_system_id
      };

      this.$vs.loading();
      
      _send_sms(data)
        .then(() => {
          this.$vs.notify({
            title: "Success",
            text: "SMS sent successfully",
            iconPack: "feather",
            icon: "icon-check-circle",
            color: "success",
          });
          this.smsPrompt = false;
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },

    send_email() {
      if (!this.customer_email) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please enter an email address",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
        });
        return;
      }

      
      const message2 = `${this.company_name || 'Company'}: Hello! Your ride from ${this.source} to ${this.destination} at ${this.date_time} has been booked. Booking Ref: ${this.booking_id}. Would you like a fixed price and pay by card? Click the link`;
      
      const data = {
        email: this.customer_email,
        message: message2,
        link: this.payment_link
      };

      this.$vs.loading();
      
      _send_email(data)
        .then(() => {
          this.$vs.notify({
            title: "Success",
            text: "Email sent successfully",
            iconPack: "feather",
            icon: "icon-check-circle",
            color: "success",
          });
          this.emailPrompt = false;
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },

    handleError(error) {
      if (!error.response) {
        this.$vs.notify({
          title: "Error",
          text: error.message || "An unexpected error occurred",
          iconPack: "feather",
          icon: "icon-cross-circle",
          color: "danger",
        });
        return;
      }

      if (error.response.status === 422) {
        this.$vs.notify({
          title: "Validation Error",
          text: (error.response.data && error.response.data.responseMessage) || "Please check your input",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
        });
      } else {
        this.$vs.notify({
          title: (error.response.data && error.response.data.responseTitle) || "Error",
          text: (error.response.data && error.response.data.responseMessage) || "An error occurred",
          iconPack: "feather",
          icon: "icon-cross-circle",
          color: "danger",
        });
      }
    },

    handleCatch(error) {
      this.$vs.notify({
        title: "Error",
        text: error.message || "An unexpected error occurred",
        iconPack: "feather",
        icon: "icon-cross-circle",
        color: "danger",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.subscription {
  padding: 1rem 0;
}

.booking-id-section {
  padding: 0.5rem 0;
  
  .section-description {
    font-size: 0.875rem;
    color: #6c757d;
    margin: 0 0 1rem 0;
    line-height: 1.4;
  }
  
  .booking-input {
    width: 100%;
    margin-bottom: 1rem;
    
    ::v-deep .vs-input {
      border-radius: 0.375rem;
    }
    
    ::v-deep .vs-input__icon {
      color: #7367f0;
    }
  }
  
  .generate-button {
    width: 100%;
    font-weight: 500;
  }
}

.booking-details-card {
  margin-top: 1rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  
  .detail-item {
    display: flex;
    align-items: center;
    padding: 0.625rem 0.75rem;
    margin-bottom: 0.5rem;
    background: #ffffff;
    border-radius: 0.375rem;
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    &:hover {
      border-color: #7367f0;
      box-shadow: 0 1px 3px rgba(115, 103, 240, 0.1);
    }
    
    &.payment-link-item {
      background: linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%);
      border-color: #764ba2;
      
      &:hover {
        border-color: #764ba2;
        box-shadow: 0 1px 4px rgba(118, 75, 162, 0.2);
      }
    }
    
    &.price-item {
      background: #ffffff;
      
      &:hover {
        border-color: #7367f0;
        box-shadow: 0 1px 3px rgba(115, 103, 240, 0.1);
      }
    }
    
    .detail-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.375rem;
      margin-right: 0.75rem;
      flex-shrink: 0;
      
      &.payment-link-icon {
        background: #f3e5f5;
        color: #764ba2;
      }
      
      &.pickup-icon {
        background: #e3f2fd;
        color: #1976d2;
      }
      
      &.destination-icon {
        background: #f3e5f5;
        color: #7b1fa2;
      }
      
      &.time-icon {
        background: #fff3e0;
        color: #f57c00;
      }
      
      &.pickup-time-icon {
        background: #e1f5fe;
        color: #0277bd;
      }
      
      &.price-icon {
        background: #e8f5e9;
        color: #28c76f;
      }
      
      svg {
        stroke-width: 2.5;
      }
    }
    
    .detail-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      
      .detail-label {
        font-size: 0.6875rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        margin-bottom: 0.125rem;
      }
      
      .detail-value {
        font-size: 0.875rem;
        color: #2c2c2c;
        font-weight: 500;
        line-height: 1.3;
        
        &.payment-link-value {
          color: #764ba2;
          text-decoration: none;
          word-break: break-all;
          display: inline-flex;
          align-items: center;
          
          &:hover {
            text-decoration: underline;
            color: #667eea;
          }
          
          svg {
            vertical-align: middle;
            color: #764ba2;
          }
        }
        
        &.price-value {
          font-weight: 700;
          color: #28c76f;
          font-size: 1rem;
        }
      }
    }
  }
}

.options-alignment {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e4e4e4;
  
  & > * {
    min-width: 150px;
  }
}

.con-exemple-prompt {
  padding: 0.5rem 0;
}
</style>
